import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { track } from "@/lib/api";
import * as Icons from "lucide-react";

function Icon({ name, className }) {
  const C = Icons[name] || Icons.Circle;
  return <C className={className} />;
}

export default function Home() {
  const [sectors, setSectors] = useState([]);
  useEffect(() => {
    api.get("/sectors").then(r => setSectors(r.data)).catch(()=>{});
    track("landing_view", { page: "home" });
  }, []);

  return (
    <div>
      {/* HERO */}
      <section className="ic-hero-grid border-b border-[#E5E4EB]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 pb-24 grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7">
            <span className="ic-badge status-coming mb-6" data-testid="hero-eyebrow">
              <span className="w-1.5 h-1.5 rounded-full bg-[#FF6A24]" /> Piattaforma SaaS per micro e piccole imprese
            </span>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.05] text-[#171522]" data-testid="hero-title">
              Conosci la tua impresa.<br/>
              <span className="text-[#5424D5]">Miglior</span><span className="text-[#FF6A24]">ala.</span>
            </h1>
            <p className="mt-6 text-lg sm:text-xl text-[#524F5E] max-w-xl leading-relaxed">
              Numeri, insight e decisioni per chi guida ogni giorno la propria attività. Analizza gratis margini, cassa e produttività — e scopri dove puoi crescere.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/business-check" className="ic-btn-primary" data-testid="hero-cta-primary">Analizza gratis la mia impresa</Link>
              <Link to="/come-funziona" className="ic-btn-ghost" data-testid="hero-cta-secondary">Come funziona</Link>
            </div>
            <div className="mt-10 flex gap-8 text-sm text-[#524F5E]">
              <div><b className="text-[#171522] text-2xl block font-bold">9+</b>Settori attivi</div>
              <div><b className="text-[#171522] text-2xl block font-bold">100%</b>Made in Italy</div>
              <div><b className="text-[#171522] text-2xl block font-bold">3 min</b>Per il primo Check</div>
            </div>
          </div>
          <div className="lg:col-span-5">
            <div className="ic-card p-6 relative overflow-hidden">
              <span className="ic-badge status-active mb-3"><span className="w-1.5 h-1.5 rounded-full bg-[#10B981]" />Anteprima del Business Check</span>
              <div className="flex items-end justify-between mt-4">
                <div>
                  <p className="text-xs uppercase tracking-widest text-[#7A7788]">Score complessivo</p>
                  <p className="text-6xl font-extrabold text-[#5424D5]">74</p>
                </div>
                <div className="text-right">
                  <p className="text-xs text-[#7A7788]">vs media settore</p>
                  <p className="text-lg font-bold text-[#FF6A24]">+8 pt</p>
                </div>
              </div>
              <div className="mt-6 h-2 bg-[#F4F4F7] rounded-full overflow-hidden">
                <div className="h-full bg-gradient-to-r from-[#5424D5] to-[#FF6A24]" style={{width:"74%"}} />
              </div>
              <ul className="mt-6 space-y-3 text-sm">
                {["Food cost sopra il target: -3 pt potenziali","Rotazione magazzino sotto media: liquidità bloccata","Scontrino aperitivo: +14% recuperabili"].map((t,i) => (
                  <li key={i} className="flex gap-3">
                    <span className="mt-1 w-2 h-2 rounded-full bg-[#FF6A24]" />
                    <span className="text-[#524F5E]">{t}</span>
                  </li>
                ))}
              </ul>
              <p className="text-[10px] uppercase tracking-widest text-[#7A7788] mt-6">Esempio illustrativo — il tuo report userà i tuoi dati reali</p>
            </div>
          </div>
        </div>
      </section>

      {/* SECTORS */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <div className="flex items-end justify-between mb-10 flex-wrap gap-4">
          <div>
            <span className="ic-badge mb-3 border-[#E5E4EB] text-[#5424D5]">SETTORI</span>
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight">Ogni settore ha i suoi numeri.<br/>Noi li leggiamo per te.</h2>
          </div>
          <Link to="/settori" className="ic-btn-ghost text-sm" data-testid="see-all-sectors">Vedi tutti i settori →</Link>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="home-sectors-grid">
          {sectors.slice(0,6).map(s => (
            <Link key={s.slug} to={`/settori/${s.slug}`} className="ic-card p-6 block group" data-testid={`sector-card-${s.slug}`}>
              <div className="w-11 h-11 rounded-xl bg-[#EDE8FC] text-[#5424D5] flex items-center justify-center mb-4 group-hover:bg-[#5424D5] group-hover:text-white transition">
                <Icon name={s.icon} className="w-5 h-5" />
              </div>
              <p className="text-xs uppercase tracking-widest text-[#7A7788] mb-1">{s.category}</p>
              <h3 className="text-lg font-semibold text-[#171522]">{s.name}</h3>
              <p className="text-sm text-[#524F5E] mt-2 leading-relaxed">{s.description}</p>
              <span className="mt-4 inline-flex items-center text-sm font-semibold text-[#5424D5]">Analizza →</span>
            </Link>
          ))}
        </div>
      </section>

      {/* VALUE PROPS */}
      <section className="bg-white border-y border-[#E5E4EB] py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          {[
            {i:"BarChart3",t:"Analizza i tuoi numeri",d:"Bastano 4 dati per una diagnosi immediata della salute della tua impresa."},
            {i:"Search",t:"Capisci cosa succede",d:"KPI e benchmark spiegati in italiano, senza gergo da bilancio."},
            {i:"Lightbulb",t:"Scopri nuove opportunità",d:"Insight prioritari per recuperare margine e liberare cassa."},
            {i:"TrendingUp",t:"Migliora i risultati",d:"Simulatori What-If, business plan e AI Advisor (in arrivo)."},
          ].map((v,i)=>(
            <div key={i} className="p-6" data-testid={`value-${i}`}>
              <div className="w-10 h-10 rounded-lg bg-[#FFF0E8] text-[#FF6A24] flex items-center justify-center mb-4">
                <Icon name={v.i} className="w-5 h-5" />
              </div>
              <h4 className="font-semibold text-lg mb-2">{v.t}</h4>
              <p className="text-sm text-[#524F5E] leading-relaxed">{v.d}</p>
            </div>
          ))}
        </div>
      </section>

      {/* €1.000 PREVIEW */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-4">
        <div className="ic-card p-8 border-[#FF6A24]/40 bg-gradient-to-br from-white to-[#FFF0E8] flex flex-wrap items-center justify-between gap-6" data-testid="home-earn1000">
          <div className="max-w-2xl">
            <span className="ic-badge status-coming mb-3">Coming Soon · Piano GROW</span>
            <h3 className="text-2xl md:text-3xl font-bold">Come posso guadagnare €1.000 in più al mese?</h3>
            <p className="text-[#524F5E] mt-2">ImpresaChiara analizzerà le leve disponibili nella tua attività e simulerà quali azioni possono avere il maggiore impatto.</p>
          </div>
          <Link to="/business-check" className="ic-btn-primary">Inizia dal Business Check</Link>
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <div className="ic-card p-10 md:p-14 bg-[#171522] text-white border-none flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
          <div>
            <p className="text-xs uppercase tracking-widest text-[#FF6A24] mb-3">Numeri. Insight. Decisioni. Crescita.</p>
            <h3 className="text-2xl md:text-3xl font-bold">Più chiarezza. Più possibilità.</h3>
            <p className="mt-2 text-white/70 max-w-xl">Inizia gratis con il Business Check. Nessuna carta, nessun impegno.</p>
          </div>
          <Link to="/business-check" className="ic-btn-accent" data-testid="footer-cta">Fai ora il Business Check</Link>
        </div>
      </section>
    </div>
  );
}
